import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '@/config/api';
import {
  Brain, X, Eye, History, Loader2, XCircle, AlertTriangle,
  Sparkles, Target, Users, BookMarked, Shield, BarChart2,
  ChevronUp, ChevronDown, Layers
} from 'lucide-react';

// Signal List Component (for BSP signals)
const SignalList = ({ signals, icon: Icon, iconColor, label }) => {
  if (!signals || signals.length === 0) return null;
  
  return (
    <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        {label} ({signals.length})
      </h3>
      <div className="space-y-2">
        {signals.map((signal, idx) => (
          <div key={idx} className="flex items-center justify-between">
            <span className="text-sm text-slate-300">{signal.label}</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${iconColor.replace('text-', 'bg-')}/60 rounded-full`}
                  style={{ width: `${signal.weight * 100}%` }}
                />
              </div>
              <span className="text-xs text-slate-500 w-10 text-right">
                {(signal.weight * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BSPDetailModal = ({ isOpen, onClose, manuscriptId, bookTitle, apiBase = '/api/admin', showHistory = true }) => {
  const { accessToken: token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bspData, setBspData] = useState(null);
  const [bspHistory, setBspHistory] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  useEffect(() => {
    if (isOpen && manuscriptId) {
      fetchBSPData();
    }
  }, [isOpen, manuscriptId]);

  const fetchBSPData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      // Build fetch URL based on apiBase
      // Admin: /api/admin/bsp/{id} and /api/admin/bsp/{id}/history
      // Creator: /api/creator/manuscripts/{id}/bsp and /api/creator/manuscripts/{id}/bsp/history
      const isAdmin = apiBase === '/api/admin';
      const bspUrl = isAdmin
        ? `${API_URL}${apiBase}/bsp/${manuscriptId}`
        : `${API_URL}${apiBase}/${manuscriptId}/bsp`;

      const fetches = [fetch(bspUrl, { headers })];

      if (showHistory) {
        const historyUrl = isAdmin
          ? `${API_URL}${apiBase}/bsp/${manuscriptId}/history`
          : `${API_URL}${apiBase}/${manuscriptId}/bsp/history`;
        fetches.push(fetch(historyUrl, { headers }));
      }

      const results = await Promise.all(fetches);
      const currentRes = results[0];
      const historyRes = results[1];

      if (currentRes.ok) {
        const data = await currentRes.json();
        // Unwrap: creator endpoint returns { status, bsp: {...} }, admin returns BSP directly
        setBspData(data.bsp || data);
      } else if (currentRes.status === 404) {
        setBspData(null);
      } else {
        throw new Error('Failed to fetch BSP data');
      }

      if (historyRes && historyRes.ok) {
        const histData = await historyRes.json();
        setBspHistory(histData.versions || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Version detection - support all BSP versions including v2.0
  const isV200 = bspData?.bsp_version === 'bsp_v2_0_llm' || bspData?.bsp_version?.startsWith('bsp_v2');
  const isV130 = bspData?.bsp_version === 'bsp_v1_3_0';
  const isV120 = bspData?.bsp_version === 'bsp_v1_2_0';
  const isV111 = bspData?.bsp_version === 'bsp_v1_1_1';
  const isV11 = bspData?.bsp_version === 'bsp_v1_1' || isV111 || isV120 || isV130 || isV200;
  const hasReaderDimensions = isV130 || isV200;

  const getPrimaryFormat = () => {
    const format = bspData?.format || {};
    if (format.nonfiction >= 0.8) return { label: 'Nonfiction', color: 'text-blue-400' };
    if (format.fiction >= 0.8) return { label: 'Fiction', color: 'text-purple-400' };
    return { label: 'Hybrid', color: 'text-cyan-400' };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="bsp-detail-modal">
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">BSP Detail View</h2>
              <p className="text-sm text-slate-400 truncate max-w-md">{bookTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('current')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'current' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Current BSP
            </span>
          </button>
          {showHistory && (
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'history' 
                  ? 'text-purple-400 border-b-2 border-purple-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Version History ({bspHistory.length})
              </span>
            </button>
          )}
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-400">{error}</p>
            </div>
          ) : activeTab === 'current' ? (
            bspData ? (
              <div className="space-y-6">
                {/* BSP Metadata */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">BSP Version</p>
                    <p className={`text-sm font-medium ${isV120 ? 'text-violet-400' : isV111 ? 'text-emerald-400' : isV11 ? 'text-cyan-400' : 'text-amber-400'}`}>
                      {bspData.bsp_version}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Pipeline</p>
                    <p className="text-sm font-medium text-white">{bspData.pipeline_version}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Confidence</p>
                    <p className="text-sm font-medium text-emerald-400">{(bspData.confidence * 100).toFixed(0)}%</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Quality Gate</p>
                    <p className={`text-sm font-medium ${bspData.quality_gate_passed ? 'text-emerald-400' : 'text-red-400'}`}>
                      {bspData.quality_gate_passed ? 'Passed' : 'Failed'}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Format</p>
                    <p className={`text-sm font-medium ${getPrimaryFormat().color}`}>
                      {getPrimaryFormat().label}
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Words</p>
                    <p className="text-sm font-medium text-white">
                      {(bspData.narrative_structure?.word_count || bspData.narrative_structure?.total_word_count || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Semantic Themes */}
                {isV11 && bspData.semantic_themes?.length > 0 && (
                  <div className="bg-gradient-to-br from-purple-900/20 to-slate-900/40 rounded-2xl border border-purple-500/20 p-5">
                    <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      Semantic Themes ({bspData.semantic_themes.length})
                    </h3>
                    <div className="space-y-2">
                      {bspData.semantic_themes.map((theme, idx) => (
                        <div key={idx} className="flex items-center justify-between group">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-sm text-white font-medium truncate">{theme.label}</span>
                            {theme.coverage && (
                              <span className="text-xs text-slate-500 flex-shrink-0">
                                ({(theme.coverage * 100).toFixed(0)}% coverage)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                                style={{ width: `${theme.weight * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-400 w-10 text-right">
                              {(theme.weight * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Topics (v1.1.1) */}
                {isV111 && bspData.topics?.length > 0 && (
                  <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-indigo-400" />
                      Topics ({bspData.topics.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {bspData.topics.map((topic, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-indigo-500/10 text-indigo-300 text-sm rounded-lg border border-indigo-500/20"
                        >
                          {topic.label}
                          <span className="ml-1 opacity-50">({(topic.weight * 100).toFixed(0)}%)</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* v1.1 Signal Grids */}
                {isV11 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SignalList 
                      signals={bspData.reader_intents} 
                      icon={Target} 
                      iconColor="text-blue-400" 
                      label="Reader Intents"
                    />
                    <SignalList 
                      signals={bspData.audience_signals} 
                      icon={Users} 
                      iconColor="text-cyan-400" 
                      label="Audience Signals"
                    />
                    <SignalList 
                      signals={bspData.genre_signals} 
                      icon={BookMarked} 
                      iconColor="text-green-400" 
                      label="Genre Signals"
                    />
                    
                    {/* Content Flags */}
                    {bspData.content_flags && Object.keys(bspData.content_flags).length > 0 && (
                      <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-amber-400" />
                          Content Flags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(bspData.content_flags).map((flag, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-1 bg-amber-500/10 text-amber-300 text-xs rounded-md border border-amber-500/20"
                            >
                              {flag.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Diagnostics Section */}
                {isV11 && (
                  <div className="border border-slate-700 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setShowDiagnostics(!showDiagnostics)}
                      className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                    >
                      <span className="text-sm text-slate-400 flex items-center gap-2">
                        <BarChart2 className="w-4 h-4" />
                        Diagnostics (Lexical Data)
                      </span>
                      {showDiagnostics ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                    
                    {showDiagnostics && (
                      <div className="p-4 space-y-4 bg-slate-900/50">
                        <p className="text-xs text-slate-500 italic mb-3">
                          Lexical themes are diagnostic only and excluded from recommendation ranking.
                        </p>
                        
                        {bspData.lexical_themes?.length > 0 && (
                          <div>
                            <p className="text-xs text-slate-400 mb-2">Lexical Themes (Top 25)</p>
                            <div className="flex flex-wrap gap-1">
                              {bspData.lexical_themes.slice(0, 25).map((t, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-slate-700/50 text-slate-400 text-xs rounded">
                                  {t.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Governance Note */}
                <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                  <p className="text-xs text-slate-500 italic">
                    BSP is a sealed, append-only semantic profile derived exclusively from NFD.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-slate-300">No BSP record found for this manuscript</p>
                <p className="text-sm text-slate-500 mt-1">BSP may not have been generated yet</p>
              </div>
            )
          ) : (
            // History Tab
            <div className="space-y-3">
              {bspHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400">No BSP history available</p>
                </div>
              ) : (
                bspHistory.map((version, idx) => (
                  <div
                    key={version.id || idx}
                    className={`p-4 rounded-lg border ${
                      version.is_current 
                        ? 'bg-purple-500/10 border-purple-500/30' 
                        : 'bg-slate-800/30 border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          version.bsp_version === 'bsp_v1_1_1' ? 'text-emerald-400' 
                            : version.bsp_version === 'bsp_v1_1' ? 'text-cyan-400' 
                            : 'text-amber-400'
                        }`}>
                          {version.bsp_version}
                        </span>
                        {version.is_current && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                            Current
                          </span>
                        )}
                        {version.quality_gate_passed === true && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                            QG
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(version.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span>Pipeline: {version.pipeline_version}</span>
                      <span>Confidence: {(version.confidence * 100).toFixed(0)}%</span>
                      <span>
                        Themes: {version.semantic_themes?.length || version.themes?.length || 0}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BSPDetailModal;
